import {refreshValidatorFields} from "./validate";
import {callFetch, getValidationRules, hideAndDisableFormFields, parseJsonData} from "./helpers";

/**
 * Displays a notification with the specified type, title, and message.
 * @param type Notification type (e.g., 'info', 'success').
 * @param title Title of the notification.
 * @param message Message body of the notification.
 */
export function notify(type, title, message) {
    if (typeof window.stackBottomRight === 'undefined') {
        window.stackBottomRight = new Pnotify.Stack({
            dir1: 'up',
            dir2: 'left',
            firstpos1: 25,
            firstpos2: 25,
            spacing1: 10,
            push: 'bottom',
            maxOpen: 3,
            modal: false
        });
    }

    Pnotify.alert({
        type: type,
        title: title,
        text: message,
        stack: window.stackBottomRight,
        delay: 4000,
        animation: 'fade',
        textTrusted: true,
        titleTrusted: true,
        sticker: false,
        closerHover: false
    });
}

/**
 * Handles the collapsing and expanding of form fields based on a selected item.
 * @param selectItem The selected item that triggers field visibility changes.
 */
export function handleSelectCollapse(selectItem) {
    const value = selectItem.value;
    const form = selectItem.closest('form');
    const overlay = document.querySelector(`#${form.dataset.overlayId}`);

    let rules = getValidationRules(form);

    // Hide all fields initially and disable them
    hideAndDisableFormFields(form);

    if (value) {
        // Ensure overlay is visible
        if (overlay) {
            overlay.classList.remove('d-none');
        }

        callFetch(
            selectItem.dataset.toggleUrl,
            'POST',
            {
                body: {
                    category: value,
                    _token: CSRF_TOKEN
                },
                headers: { "Content-Type": "application/json;charset=UTF-8" }
            },
            data => {
                // Extract and parse JSON part from response data
                const responseData = parseJsonData(data);

                if (responseData) {
                    // Show/hide fields and update validation rules
                    showToggleFields(responseData['toggle-fields'], form);
                    setToggleFieldAlert(responseData['alert']);
                    refreshValidatorFields(rules);
                }

                // Hide overlay with animation handled by CSS
                if (overlay) {
                    overlay.classList.remove('show');
                    overlay.addEventListener('transitionend', () => {
                        overlay.classList.add('d-none');
                    }, { once: true });
                }
            }
        );
    }
}

/**
 * Function to handle toggling fields based on the response data
 * @param toggleFields
 * @param form
 */
function showToggleFields(toggleFields, form) {
    // Assume toggleFields is an array of field IDs to be shown
    toggleFields.forEach(fieldId => {
        const field = form.querySelector(`#${fieldId}`);
        if (field) {
            field.classList.remove('hidden');
            field.querySelectorAll('input, select, textarea').forEach(input => {
                input.disabled = false;
            });
        }
    });
}

/**
 * Sets an alert message for the form.
 * @param alert The alert message to display.
 */
function setToggleFieldAlert(alert) {
    if (alert !== null) {
        const alertFieldElement = document.querySelector('#alert-field.select-collapse');
        const contentElement = alertFieldElement?.querySelector('.alert div.content');

        if (alertFieldElement && contentElement) {
            alertFieldElement.classList.remove('hidden');
            contentElement.innerHTML = alert;
        }
    }
}

/**
 * Shows a global overlay with a loader animation.
 * @param container The container element for the overlay (not used in this function).
 */
export function showGlobalOverlay(container) {
    const loader = document.querySelector('#global-loader');

    if (loader !== null) {
        loader.classList.add('show');

        setTimeout(() => {
            loader.classList.remove('show');
        }, 200);
    }
}

/**
 * Submits the form when the submit button is clicked, with optional validation and callback.
 * @param callback Optional callback function to execute before form submission.
 */
export function submitForm(callback) {
    const submitButton = document.querySelector('button.form-submit-button');
    if (submitButton !== null) {
        submitButton.addEventListener('click', (event) => {
            const _this = event.target;
            const form = event.currentTarget.closest('form');

            if (
                typeof Validator === 'undefined'
                || (typeof Validator !== 'undefined' && Validator.isFormValid())
            ) {
                if (callback) {
                    callback(_this, form, event);
                } else {
                    document.querySelector(`#${form.dataset.overlayId}`)?.classList.remove('d-none');
                    form.submit();
                }
            }
        });
    }
}

/**
 * Handles the submission of a form with reCAPTCHA validation.
 *
 * This function sets the reCAPTCHA token value to an input field in the form,
 * and submits the form if the token is successfully generated.
 *
 * @param {HTMLElement} button - The button that triggers the form submission.
 * @param {HTMLFormElement} form - The form being submitted.
 * @param {Event} event - The event object for the form submission.
 */
export function handleRecaptchaOnSubmit(button, form, event) {
    let action = button.dataset.recaptchaAction;

    event.preventDefault();

    const overlayElement = document.querySelector(`#${form.dataset.overlayId}`);

    if (overlayElement) {
        // Show the overlay
        overlayElement.classList.add('visible');
        overlayElement.classList.remove('hidden');
    }

    if (button.dataset.recaptcha && action) {
        grecaptcha.ready(() => {
            grecaptcha.execute(RECAPTCHA_SITE_KEY, { action: action }).then(token => {
                let recaptchaInput = form.querySelector('input[name="recaptcha-token"]');

                if (recaptchaInput) {
                    recaptchaInput.value = token;
                    form.submit();
                } else if (overlayElement) {
                    // Hide the overlay if recaptchaInput is not found
                    overlayElement.classList.add('hide');
                    overlayElement.classList.remove('show');
                }

            }).catch(error => {
                if (overlayElement) {
                    // Hide the overlay in case of error
                    overlayElement.classList.add('hide');
                    overlayElement.classList.remove('show');
                }
            });
        });
    } else if (overlayElement) {
        // Hide the overlay if recaptcha is not available
        overlayElement.classList.add('hide');
        overlayElement.classList.remove('show');
    }
}

/**
 * Loads the reCAPTCHA API by dynamically creating a script element and appending it to the head of the document.
 *
 * @return {void} - This method does not return anything.
 */
export function loadRecaptchaApi() {
    let script = document.createElement('script');
    script.src = `${RECAPTCHA_JS_URL}?render=${RECAPTCHA_SITE_KEY}`;
    script.async = true;

    document.head.appendChild(script);
}
